import * as React from 'react';

import { ApplicationHelpType, LookupDataEnums } from '../../../utilities/Constants';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import { Stack, Table } from 'react-bootstrap';

import CheckmateNSelect from '../../../components/shared/CheckmateNSelect';
import Common from '../../../stores/Common';
import { Help } from '../../../components/shared/Help';
import { IAlertSearchModel } from '../../../interfaces/INote';
import { IPackageSettlementSearchModel } from '../../../interfaces/IPackageSettlement';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import { useGetLookupData } from '../../../shared/react-query-hooks/useGetLookupData';
import { useGetPlaintiffsFirms } from '../../../shared/react-query-hooks/useGetPlaintiffsFirms';

export interface IPackageListSearchFiltersProps {
    appliedSearchModel: IPackageSettlementSearchModel;
    isSearching: boolean;
    onApplyFilters: (searchModel: IPackageSettlementSearchModel) => void;
}

function lookupItemToMultiSelectOptionMapper(lookupItem: ILookupModel) {
    const value =
        lookupItem.guid === '00000000-0000-0000-0000-000000000000' || !lookupItem.guid
            ? lookupItem.id
            : lookupItem.guid;

    return {
        id: lookupItem.id,
        label: lookupItem.displayName,
        value,
    } as IMultiSelectOption;
}

export function PackageListSearchFilters(props: IPackageListSearchFiltersProps) {
    const packageDealStatuses = useGetLookupData(LookupDataEnums.PackageSettlementStatusTypes);
    const plaintiffsFirms = useGetPlaintiffsFirms();

    const [pendingSearchModel, setPendingSearchModel] =
        React.useState<IPackageSettlementSearchModel>(props.appliedSearchModel);

    function handleStatusChange(selectedOptions: IMultiSelectOption[]): void {
        setPendingSearchModel((prev) => ({
            ...prev,
            statusIds: selectedOptions.map((opt) => Number(opt.id)),
        }));
    }

    function handlePlaintiffsFirmFilterChange(selectedOptions: IMultiSelectOption[]): void {
        setPendingSearchModel((prev) => ({
            ...prev,
            plaintiffsFirmIds: selectedOptions.map((opt) => opt.value ?? ''),
        }));
    }

    function handleStartDateChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const newValue = event.target.value;
        setPendingSearchModel((prev) => ({
            ...prev,
            startDate: newValue,
        }));
    }

    function handleEndDateChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const newValue = event.target.value;
        setPendingSearchModel((prev) => ({
            ...prev,
            endDate: newValue,
        }));
    }

    function handleApplyFiltersClick(): void {
        props.onApplyFilters(pendingSearchModel);
    }

    function handleClearFiltersClick(): void {
        setPendingSearchModel({});
    }

    const statusOptions = packageDealStatuses.data?.map(lookupItemToMultiSelectOptionMapper);
    const plaintiffsFirmsOptions = plaintiffsFirms.data?.map(lookupItemToMultiSelectOptionMapper);

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Status
                            </td>
                            <td>
                                <CheckmateNSelect
                                    options={statusOptions ?? []}
                                    value={
                                        statusOptions?.filter((opt) =>
                                            pendingSearchModel.statusIds?.includes(opt.id)
                                        ) ?? []
                                    }
                                    onChange={handleStatusChange}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Plaintiffs Firm
                            </td>
                            <td>
                                <CheckmateNSelect
                                    options={plaintiffsFirmsOptions ?? []}
                                    value={
                                        plaintiffsFirmsOptions?.filter((opt) =>
                                            pendingSearchModel.plaintiffsFirmIds?.includes(
                                                opt.value ?? ''
                                            )
                                        ) ?? []
                                    }
                                    onChange={handlePlaintiffsFirmFilterChange}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125, paddingTop: 8 }} className="nowrap">
                                Start Date
                                <Help
                                    type={ApplicationHelpType.Info}
                                    title="Date Range"
                                    helpText="The date range filter/criteria looks at the Final Date if the deal status is Complete; for all other statuses, the filter/criteria looks at Initial Date."
                                />
                            </td>
                            <td>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={pendingSearchModel.startDate ?? ''}
                                    onChange={handleStartDateChange}
                                    className={
                                        'form-control d-inline-block' +
                                        (pendingSearchModel.startDate &&
                                        Common.isValidDate(pendingSearchModel.startDate)
                                            ? ''
                                            : ' unselectClass')
                                    }
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125, paddingTop: 8 }} className="nowrap">
                                End Date
                                <Help
                                    type={ApplicationHelpType.Info}
                                    title="Date Range"
                                    helpText="The date range filter/criteria looks at the Final Date if the deal status is Complete; for all other statuses, the filter/criteria looks at Initial Date."
                                />
                            </td>
                            <td>
                                <input
                                    type="date"
                                    name="endDate"
                                    value={pendingSearchModel.endDate ?? ''}
                                    onChange={handleEndDateChange}
                                    className={
                                        'form-control d-inline-block' +
                                        (pendingSearchModel.endDate &&
                                        Common.isValidDate(pendingSearchModel.endDate)
                                            ? ''
                                            : ' unselectClass')
                                    }
                                />
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    disabled={props.isSearching}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />
                <button className="btn btn-default" onClick={handleClearFiltersClick}>
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
}
